import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import SelfOrderLanding from './screens/SelfOrderLanding';
// Note: serviceWorkerRegistration is intentionally not imported — admin
// SPA explicitly does NOT register a service worker. The CRA scaffolding
// file (src/serviceWorkerRegistration.js) remains in the tree for future
// opt-in but is not wired anywhere. See decommission block below.
import reportWebVitals from './reportWebVitals';

// Public customer-facing route: /t/<HOTELID-OUTLET-TABLE-SALT>
// Rendered OUTSIDE the staff App shell so scanning the QR never lands
// the customer on the staff login page. Keep this detection in index.js
// so the decision happens once, before React mounts the staff root.
const parsePublicQr = () => {
  // eslint-disable-next-line no-useless-escape
  const match = /^\/t\/([A-Za-z0-9_\-]+)$/i.exec(window.location.pathname || '');
  return match ? match[1] : null;
};
const publicQrCode = parsePublicQr();

// Decommission any previously-registered service worker for BOTH the staff
// app and the public QR landing, and purge its caches. Reason: every staff
// deploy was being held hostage by the SW cache — users had to manually
// unregister via DevTools to see new bundles. For a staff SPA we need fresh
// data every session anyway; SW offline/caching is net-negative.
//
// This block runs on EVERY page load for existing installs — once an
// existing client runs this once, its SW + caches are gone and it
// auto-upgrades from then on.
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(regs => {
    for (const r of regs) r.unregister().catch(() => {});
  }).catch(() => {});
  if (typeof caches !== 'undefined' && typeof caches.keys === 'function') {
    caches.keys().then(keys => {
      for (const k of keys) caches.delete(k).catch(() => {});
    }).catch(() => {});
  }
}

const API_BASE_URL = String(process.env.REACT_APP_API_BASE_URL || '').trim().replace(/\/+$/, '');

if (API_BASE_URL && typeof window !== 'undefined' && typeof window.fetch === 'function') {
  const nativeFetch = window.fetch.bind(window);
  window.fetch = (input, init) => {
    if (typeof input === 'string') {
      const isAbsolute = /^https?:\/\//i.test(input);
      if (!isAbsolute && input.startsWith('/')) {
        return nativeFetch(`${API_BASE_URL}${input}`, init);
      }
      return nativeFetch(input, init);
    }

    if (input instanceof Request) {
      const isAbsolute = /^https?:\/\//i.test(input.url);
      if (!isAbsolute && input.url.startsWith('/')) {
        const proxiedRequest = new Request(`${API_BASE_URL}${input.url}`, input);
        return nativeFetch(proxiedRequest, init);
      }
      return nativeFetch(input, init);
    }

    return nativeFetch(input, init);
  };
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    {publicQrCode
      ? <SelfOrderLanding qrCode={publicQrCode} />
      : <App />}
  </React.StrictMode>
);

// Service worker is intentionally NOT registered — see the decommission
// block at the top of this file for rationale. Admin SPA needs fresh data
// every session; the stale-cache-on-deploy friction outweighed the caching
// benefits. Do not re-add `serviceWorkerRegistration.register()` here
// without owner sign-off.

// Core Web Vitals (LCP/INP/CLS + FCP/TTFB) to the console for now — see
// reportWebVitals.js. No analytics endpoint wired yet; escalate to one if
// this needs to be tracked across real devices rather than read ad hoc.
reportWebVitals(({ name, value, rating, id }) => {
  // eslint-disable-next-line no-console
  console.log(`[web-vitals] ${name}: ${Math.round(value * 100) / 100} (${rating})`, { id });
});
